import {useEffect, useState} from "react";
import {useParams, Link} from "react-router-dom";
import {getFromApi, deleteFromApi} from "../../api/auth";
import AddNote from "../../components/notes/AddNote";
import LinkedTodo from "../../components/notes/LinkedTodo";
import ConfirmButton from "../../components/shared/ConfirmButton";
import {summarize, extractKeywords, readingTime} from "../../utils/summarize";

function SummaryPanel({content}) {
    const summary  = summarize(content, 3);
    const keywords = extractKeywords(content, 8);
    return (
        <div>
            <div className="mb-3">
                <div className="small fw-semibold text-muted text-uppercase mb-2" style={{letterSpacing:"0.05em"}}>
                    <i className="bi bi-stars me-1"/>Summary
                </div>
                {summary ? (
                    <p style={{lineHeight:1.75,color:"#374151"}}>{summary}</p>
                ) : (
                    <p className="text-muted small">Not enough content to summarise.</p>
                )}
            </div>
            {keywords.length > 0 && (
                <div>
                    <div className="small fw-semibold text-muted text-uppercase mb-2" style={{letterSpacing:"0.05em"}}>
                        <i className="bi bi-tags me-1"/>Keywords
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                        {keywords.map(kw => (
                            <span key={kw} className="badge rounded-pill" style={{background:"#eef2ff",color:"#4f46e5",fontWeight:500}}>{kw}</span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function NoteFolderView() {
    const {folderId} = useParams();
    const [folder, setFolder]           = useState(null);
    const [entries, setEntries]         = useState([]);
    const [linkedTodo, setLinkedTodo]   = useState(null);
    const [allTodoGroups, setAllTodoGroups] = useState([]);
    const [loading, setLoading]         = useState(true);
    const [selected, setSelected]       = useState(null);
    const [composing, setComposing]     = useState(false);
    const [editing, setEditing]         = useState(false);
    const [showSummary, setShowSummary] = useState(false);
    const [search, setSearch]           = useState("");

    const load = async () => {
        try {
            const [folderRes, todoRes] = await Promise.all([
                getFromApi(`api/notes/${folderId}`),
                getFromApi("api/todo"),
            ]);
            setFolder(folderRes.data?.folder || null);
            setEntries(folderRes.data?.entries || []);
            setLinkedTodo(folderRes.data?.linkedTodo || null);
            setAllTodoGroups(todoRes.data || []);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [folderId]);

    const handleDeleteEntry = async (entryId) => {
        await deleteFromApi(`api/notes/entry/${entryId}`);
        setSelected(null); setEditing(false);
        load();
    };

    const formatDate = (iso) => {
        if (!iso) return "";
        return new Date(iso).toLocaleDateString(undefined, {day:"numeric",month:"short",year:"numeric"});
    };

    const filtered = search
        ? entries.filter(e => e.title.toLowerCase().includes(search.toLowerCase()))
        : entries;

    const isOpen = composing || selected;

    if (loading) return <div className="text-muted py-4 text-center">Loading...</div>;

    return (
        <div>
            <div className="breadcrumb-nav mb-1">
                <Link to="/notes">Notes</Link> / {folder?.name || "Folder"}
            </div>
            <div className="page-header d-flex align-items-center justify-content-between">
                <h4><i className="bi bi-journal-text me-2" style={{color:"#6366f1"}}/>{folder?.name || "Notes"}</h4>
                <button className="btn btn-dark btn-sm" onClick={() => { setComposing(true); setSelected(null); setEditing(false); }}>
                    <i className="bi bi-plus-lg me-1"/>New note
                </button>
            </div>

            <div className="row g-3">
                {/* Entry list */}
                <div className={isOpen ? "col-md-4" : "col-12"}>
                    <div className="mb-3">
                        <input className="form-control form-control-sm" placeholder="Search notes..." value={search} onChange={e => setSearch(e.target.value)}/>
                    </div>

                    {!filtered.length && !composing ? (
                        <div className="empty-state">
                            <i className="bi bi-journal-plus"/>
                            <div className="fw-medium mb-1">{search ? "No matches." : "No notes yet."}</div>
                        </div>
                    ) : (
                        <div className="panel p-0" style={{overflow:"hidden"}}>
                            {filtered.map((e, i) => (
                                <button
                                    key={e._id}
                                    onClick={() => { setSelected(e); setComposing(false); setEditing(false); setShowSummary(false); }}
                                    className={[
                                        "w-100 text-start px-4 py-3 border-0 bg-transparent",
                                        i < filtered.length - 1 ? "border-bottom" : "",
                                        selected?._id === e._id ? "bg-light" : "",
                                    ].join(" ")}
                                >
                                    <div className="fw-medium text-truncate">{e.title}</div>
                                    <div className="small text-muted mt-1">{formatDate(e.created_on)}</div>
                                </button>
                            ))}
                        </div>
                    )}

                    <LinkedTodo
                        folderId={folderId}
                        todo={linkedTodo}
                        allTodoGroups={allTodoGroups}
                        onRefresh={load}
                    />
                </div>

                {/* Compose new note */}
                {composing && (
                    <div className="col-md-8">
                        <div className="panel">
                            <h6 className="fw-semibold mb-3">New note</h6>
                            <AddNote
                                folderId={folderId}
                                onSaved={() => { setComposing(false); load(); }}
                                onCancel={() => setComposing(false)}
                            />
                        </div>
                    </div>
                )}

                {/* View / edit selected note */}
                {selected && !composing && (
                    <div className="col-md-8">
                        <div className="panel">
                            {editing ? (
                                <>
                                    <h6 className="fw-semibold mb-3">Edit note</h6>
                                    <AddNote
                                        folderId={folderId}
                                        entryId={selected._id}
                                        initialTitle={selected.title}
                                        initialContent={selected.content}
                                        onSaved={() => { setEditing(false); load().then(() => {
                                            setEntries(prev => {
                                                const updated = prev.find(e => e._id === selected._id);
                                                if (updated) setSelected(updated);
                                                return prev;
                                            });
                                        }); }}
                                        onCancel={() => setEditing(false)}
                                    />
                                </>
                            ) : (
                                <>
                                    <div className="d-flex align-items-start justify-content-between mb-2">
                                        <div>
                                            <h5 className="mb-0 fw-semibold">{selected.title}</h5>
                                            <small className="text-muted">
                                                {formatDate(selected.created_on)}
                                                <span className="mx-2">·</span>
                                                {readingTime(selected.content)}
                                            </small>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <button
                                                className={`btn btn-sm ${showSummary?"btn-dark":"btn-outline-secondary"}`}
                                                onClick={() => setShowSummary(s => !s)}
                                            >
                                                <i className="bi bi-card-text me-1"/>{showSummary ? "Full note" : "Summarise"}
                                            </button>
                                            <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditing(true)}>
                                                <i className="bi bi-pencil"/>
                                            </button>
                                            <ConfirmButton className="btn btn-sm btn-outline-danger" onConfirm={() => handleDeleteEntry(selected._id)}>
                                                <i className="bi bi-trash"/>
                                            </ConfirmButton>
                                        </div>
                                    </div>
                                    <hr/>
                                    {showSummary ? (
                                        <SummaryPanel content={selected.content}/>
                                    ) : (
                                        <div style={{lineHeight:1.7}} dangerouslySetInnerHTML={{__html: selected.content}}/>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
